import { useMemo, useState } from "react";

import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";

import { UserAnalyticsService } from "@analytics/index";

import CompanyDetailsDrawer from "@components/CompanyDetailsDrawer/CompanyDetailsDrawer";
import CompanyDisplay from "@components/CompanyDetailsDrawer/CompanyDisplay";
import CompanyFilterDrawer from "@components/FilterControls/CompanyFilterDrawer";
import {
  TableToolbar,
  TableToolbarDropdown,
  TableToolbarDropdownMenuItem,
  TableToolbarFilterButton,
  TableToolbarLeft,
  TableToolbarRight,
  TableToolbarSearch,
} from "@components/TableToolbar/TableToolbar";

import useHandleCompanySelection from "@hooks/useHandleCompanySelection";

import { IFetchCompanySearch } from "@interfaces/ipsearch.type";

import useCompaniesTableConfig from "@pages/Companies/components/useCompaniesTableConfig";
import useDirectoryToolbar from "@pages/Companies/components/useDirectoryToolbar";

import { useCompaniesQuery } from "@queries/company.query";

import CompanyService from "@services/company.service";

import { useTableStore } from "@store/filters/filters.store";
import { INITIAL_FILTERS_STATES_MAP } from "@store/filters/initialStates";
import useSmartSearchStore from "@store/smartSearch";

import contentCardStyles from "@theme/contentCardStyles";

import numberOfActiveFilters from "@utilities/numberOfActiveFilters";

import CompaniesTable from "./CompaniesTable";

interface IDirectoryCompanySelectProperties {
  fetchCompanySearch: ({ preferredIndustries, queryString }: IFetchCompanySearch) => void;
  shouldTriggerModal: boolean;
}

export default function CompaniesSearch({ fetchCompanySearch, shouldTriggerModal }: IDirectoryCompanySelectProperties) {
  const {
    data: companyResults = {
      companies: [],
      currentPage: 0,
      total: 0,
      totalPages: 0,
    },
    isPlaceholderData,
    status,
  } = useCompaniesQuery();

  const { filters, pagination, selection, setTableFilters, setTablePagination, setTableSort, sort } =
    useTableStore("company");

  const handleSetSelectedCompanyIds = useHandleCompanySelection();

  const [focusedId, setFocusedId] = useState<null | string>(null);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { isSmartSearchEnabled, setIsSmartSearchFromTechnologyOverview } = useSmartSearchStore();

  const handleSetFocusedId = (id: string) => {
    setFocusedId(id);
  };

  const { handleAddCompaniesToTechnology, handleSmartSearch, toggleFilterVisibility } = useDirectoryToolbar({
    allCompanies: companyResults.companies,
    fetchCompanySearch,
    isFilterOpen,
    searchId: "",
    selectedIds: selection ?? [],
    setIsFilterOpen,
    technologyName: "",
    workflow: "smart-search",
  });

  const { columns, handleRowFocus, initialState, rows } = useCompaniesTableConfig({
    companies: companyResults.companies ?? [],
    setFocusedId: handleSetFocusedId,
  });

  const focusedCompany = useMemo(() => {
    if (focusedId) {
      return companyResults.companies.find((company) => company.id === focusedId);
    }
    return null;
  }, [focusedId, companyResults.companies]);

  if (shouldTriggerModal) {
    handleSmartSearch();
    setIsSmartSearchFromTechnologyOverview(false);
  }

  return (
    <Box sx={contentCardStyles}>
      <CompanyDetailsDrawer close={() => setFocusedId(null)} isOpen={focusedId !== null}>
        {focusedCompany && <CompanyDisplay close={() => setFocusedId(null)} company={focusedCompany} />}
      </CompanyDetailsDrawer>
      <CompanyFilterDrawer isOpen={isFilterOpen} setOpen={setIsFilterOpen} />
      <TableToolbar>
        <TableToolbarLeft>
          <Tooltip
            placement="bottom"
            title="Portfolio Search identifies relevant companies based on their patent portfolio."
          >
            <Button
              color={isSmartSearchEnabled ? "primary" : "secondary"}
              onClick={handleSmartSearch}
              startIcon={<ManageSearchIcon />}
              sx={{ borderRadius: "8px" }}
              variant="contained"
            >
              Portfolio Search
            </Button>
          </Tooltip>
          <TableToolbarSearch
            placeholder="Search Company Name"
            query={filters.search ?? ""}
            setQuery={(query) => {
              setTableFilters({ search: query });
              UserAnalyticsService.trackCompanySearchBarUsed(query);
              if (query.trim().length === 0) {
                setTableSort([{ field: "numEmployees", sort: "desc" }]);
              } else {
                setTableSort([{ field: "", sort: "asc" }]);
              }
            }}
          />
        </TableToolbarLeft>
        <TableToolbarRight>
          <TableToolbarFilterButton
            activeFilters={numberOfActiveFilters(filters, INITIAL_FILTERS_STATES_MAP.company["filters"])}
            toggleFilterVisibility={toggleFilterVisibility}
            tooltipText="Filter companies based on location, industry, and more."
          />
          <TableToolbarDropdown
            actionsLabel="Save Company"
            selectedIds={selection ?? []}
            tooltipText="Save selected companies to your Portfolio or download to CSV."
          >
            <TableToolbarDropdownMenuItem onClick={handleAddCompaniesToTechnology}>
              <ListItemIcon>
                <LightbulbRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Save To Technology</ListItemText>
            </TableToolbarDropdownMenuItem>
            <TableToolbarDropdownMenuItem
              onClick={() => CompanyService.exportCompanies(selection ?? [], companyResults.companies)}
            >
              <ListItemIcon>
                <DownloadRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export Company Data</ListItemText>
            </TableToolbarDropdownMenuItem>
          </TableToolbarDropdown>
        </TableToolbarRight>
      </TableToolbar>
      <CompaniesTable
        columns={columns}
        handlePaginationModelChange={setTablePagination}
        handleRowFocus={handleRowFocus}
        handleSortModelChange={setTableSort}
        initialState={initialState}
        isCompanySearching={status === "pending" || isPlaceholderData}
        pagination={pagination}
        paginationMode="server"
        rowCount={companyResults.total}
        rows={rows}
        selection={selection ?? []}
        setSelection={handleSetSelectedCompanyIds}
        sortingMode="server"
        sortModel={sort}
      />
    </Box>
  );
}
